import React, { useState } from 'react';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { projectCaseStudies } from '../../data/projects';
import { Badge, Card, CardTitle } from '../ui';

export const PortfolioShowcase: React.FC<{ onOpenConsultation?: () => void }> = ({ onOpenConsultation }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', 'Development Services', 'Public Sector & LGU', 'Healthcare & EMR', 'Delivery & Logistics', 'Web3 & Blockchain', 'Lead Generation'];

  const filteredProjects = selectedCategory === 'All'
    ? projectCaseStudies
    : projectCaseStudies.filter((p) => p.category === selectedCategory);

  return (
    <section id="portfolio-showcase" className="py-12 sm:py-14 lg:py-16 relative bg-[#f5f9fc] dark:bg-[#080e1a] transition-colors">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-10 gap-6">
          <div className="space-y-3 max-w-xl">
            <Badge variant="brand" className="px-3.5 py-1 text-[11px] sm:text-xs uppercase tracking-wider">
              Proven Track Record
            </Badge>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-[#184166] dark:text-white tracking-tight">
              Featured <span className="text-[#4a82c2]">Case Studies</span> & Enterprise Work
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-sm">
              Discover how Technook engineers custom mobile applications, enterprise portals, LGU public governance systems, EMR health suites, delivery logistics engines, and Web3 blockchain smart contracts.
            </p>
          </div>

          {/* Category Filter Chips */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-xs font-semibold rounded-full transition-all whitespace-nowrap ${
                  selectedCategory === cat
                    ? 'bg-[#4a82c2] text-white shadow-lg shadow-[#4a82c2]/25'
                    : 'bg-white dark:bg-[#0f1b2d] text-slate-700 dark:text-slate-300 hover:text-[#4a82c2] dark:hover:text-white border border-slate-200 dark:border-slate-800'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Project Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredProjects.map((project) => (
            <Card key={project.id} variant="interactive" className="flex flex-col justify-between p-6 space-y-6 bg-white dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 shadow-xl">
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <Badge variant="outline" className="text-[10px] sm:text-xs max-w-full whitespace-normal leading-normal">{project.clientIndustry}</Badge>
                  <span className="text-[11px] font-mono text-[#4a82c2] font-semibold shrink-0">{project.category}</span>
                </div>

                <CardTitle className="text-lg sm:text-xl font-bold text-[#184166] dark:text-white leading-snug">{project.title}</CardTitle>
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{project.summary}</p>

                {/* Metrics */}
                <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-[#f5f9fc] dark:bg-[#09101d] border border-slate-200 dark:border-slate-800">
                  {project.metrics.map((m, idx) => (
                    <div key={idx}>
                      <span className="text-[10px] text-slate-500 uppercase block font-semibold">{m.label}</span>
                      <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{m.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-slate-800/80 flex items-center justify-between">
                <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                  <CheckCircle2 size={14} className="text-emerald-500" /> Delivered
                </span>
                <button
                  onClick={onOpenConsultation}
                  className="text-xs font-bold text-[#4a82c2] hover:text-[#08adae] flex items-center gap-1 transition-colors"
                >
                  <span>Build Similar</span> <ArrowRight size={13} />
                </button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
